import Radio from '@/components/UI/FormElements/Radio';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { type FC } from 'react';
import { featuresSelector } from '@/support/Selectors';

interface RootState {
  ui: {
    settings: {
      changePercentage?: string;
    };
  };
}

const featureSelector = createSelector([featuresSelector], (features) => features?.date_based_alerts ?? false);

/**
 * Component for selecting how percentage metric changes are calculated.
 */
const MetricChangeFrom: FC = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state: RootState) => state.ui.settings);
  const dateBasedAlertsEnabled = useSelector(featureSelector);

  const updateSettings = (setting: Record<string, unknown>) => {
    dispatch({ type: 'SAVE_SETTING', setting });
  };

  return (
    <div className="pa3 bb b--moon-gray">
      <PercentageChange
        settings={settings}
        updateSettings={updateSettings}
        dateBasedAlertsEnabled={dateBasedAlertsEnabled}
      />
    </div>
  );
};

interface PercentageChangeProps {
  settings: { changePercentage?: string };
  updateSettings: (setting: Record<string, unknown>) => void;
  dateBasedAlertsEnabled?: boolean;
}

export const PercentageChange: FC<PercentageChangeProps> = ({
  settings,
  updateSettings,
  dateBasedAlertsEnabled = false,
}) => {
  const baseOptions: Record<string, string> = {
    value_change_from_first: 'First taken',
    value_change: 'Last taken',
  };

  const options: Record<string, string> = dateBasedAlertsEnabled
    ? {
        ...baseOptions,
        value_change_from_tracking_date: 'Tracking date',
        value_change_from_disease_induction_date: 'Disease induction date',
      }
    : baseOptions;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    updateSettings({ ...settings, changePercentage: event.target.value });
  };

  if (dateBasedAlertsEnabled) {
    return (
      <div className="flex items-center">
        <div className="f6 near-black lh-title mr3">% Metric change from</div>
        <select value={settings?.changePercentage ?? ''} onChange={handleChange} className="mb0">
          {Object.keys(options).map((key) => (
            <option key={key} value={key}>
              {options[key]}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className="f6 near-black lh-title mr3">% Metric change from</div>
      <div className="flex flex-wrap">
        {Object.keys(options).map((key) => (
          <div key={key} className="dib mr3">
            <Radio
              id={key}
              name="options"
              checked={settings?.changePercentage === key}
              label={options[key]}
              onChange={handleChange}
              value={key}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricChangeFrom;
